import { useEffect, useState } from "react";
import { Plus, Send, ArrowRightLeft, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const STATUS_LABELS = { brouillon: "Brouillon", envoye: "En attente", accepte: "Répondu", refuse: "Annulée", expire: "Passée" };
const STATUS_TEXT = { brouillon: "Brouillon", envoye: "Envoyé", accepte: "Accepté", refuse: "Refusé", expire: "Expiré" };

const EMPTY_FORM = { customer_email: "", customer_name: "", start_date: "", end_date: "", venue: "", product_slug: "", qty: 1, valid_days: 30, notes: "" };

export default function QuotesPage() {
  const [quotes, setQuotes] = useState(null);
  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => {
    api.get("/admin/quotes", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
    api.get("/catalog/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      customer_email: q.customer?.email || "", customer_name: `${q.customer?.first_name || ""} ${q.customer?.last_name || ""}`.trim(),
      start_date: q.event?.start_date || "", end_date: q.event?.end_date || "", venue: q.event?.venue_address || "",
      product_slug: q.items[0]?.slug || "", qty: q.items[0]?.qty || 1, valid_days: 30, notes: q.notes || "",
    });
    setOpen(true);
  };

  const payload = () => ({
    customer: { first_name: form.customer_name.split(" ")[0] || form.customer_name, last_name: form.customer_name.split(" ").slice(1).join(" ") || "—", email: form.customer_email },
    event: { type: "mariage", start_date: form.start_date, end_date: form.end_date, venue_address: form.venue },
    items: [{ product_slug: form.product_slug, qty: Number(form.qty) }],
    delivery: { mode: "retrait" },
    notes: form.notes || null,
    valid_days: Number(form.valid_days),
  });

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/admin/quotes/${editing.id}`, payload());
        toast.success("Nouvelle version du devis créée — l'ancienne version est conservée");
      } else {
        await api.post("/admin/quotes", payload());
        toast.success("Devis créé (brouillon)");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async (q) => {
    try { await api.post(`/admin/quotes/${q.id}/send`); toast.success(`Devis ${q.ref} envoyé par e-mail`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async (q) => {
    try {
      const r = await api.get(`/admin/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Devis_${q.ref}_v${q.version}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF indisponible"); }
  };

  const transform = async (q) => {
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/transform`);
      toast.success(`Transformé en réservation ${data.ref} — en attente de finalisation client`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!quotes) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="quotes-loading">Chargement…</div>;

  return (
    <div data-testid="admin-quotes-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B14 — Devis</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Devis</h1>
          <p className="mt-1 text-sm text-muted-foreground">Versionnés, jamais écrasés. Devis accepté ≠ réservation confirmée : la transformation re-contrôle la disponibilité.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="quote-create">
          <Plus className="h-4 w-4" /> Nouveau devis
        </button>
      </div>

      <div className="card-ae mt-6">
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <select className="input-ae w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="quotes-status-filter" aria-label="Filtrer par statut">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_TEXT).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </div>
        {quotes.length === 0 ? (
          <div className="p-5"><EmptyState title="Aucun devis">Créez un devis pour une demande nécessitant un chiffrage personnalisé.</EmptyState></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="quotes-table">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-5 py-3">Référence</th>
                  <th className="px-5 py-3">Client</th>
                  <th className="px-5 py-3">Période</th>
                  <th className="px-5 py-3 text-right">Total TTC</th>
                  <th className="px-5 py-3">Statut</th>
                  <th className="px-5 py-3">Validité</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {quotes.map((q) => (
                  <tr key={q.id} className="transition-colors hover:bg-cream/60" data-testid={`quote-row-${q.ref}`}>
                    <td className="px-5 py-3.5 font-semibold text-deepgreen">{q.ref} <span className="text-xs font-normal text-muted-foreground">v{q.version}</span></td>
                    <td className="px-5 py-3.5">{q.customer ? `${q.customer.first_name} ${q.customer.last_name}` : "—"}</td>
                    <td className="px-5 py-3.5 text-muted-foreground">{q.event?.start_date} → {q.event?.end_date}</td>
                    <td className="px-5 py-3.5 text-right font-semibold">{(q.totals?.total_ttc ?? 0).toFixed(2)} €</td>
                    <td className="px-5 py-3.5"><StatusBadge status={STATUS_LABELS[q.status] || q.status} /></td>
                    <td className="px-5 py-3.5 text-muted-foreground">{(q.valid_until || "").slice(0, 10)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => downloadPdf(q)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`quote-pdf-${q.ref}`}>
                          PDF
                        </button>
                        {q.status === "brouillon" && (
                          <>
                            <button type="button" onClick={() => openEdit(q)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`quote-edit-${q.ref}`}>
                              <PencilLine className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" onClick={() => send(q)} className="btn-primary-ae !px-3.5 !py-1.5 !text-[11px]" data-testid={`quote-send-${q.ref}`}>
                              <Send className="h-3.5 w-3.5" /> Envoyer
                            </button>
                          </>
                        )}
                        {q.status === "accepte" && !q.reservation_id && (
                          <button type="button" onClick={() => transform(q)} className="btn-primary-ae !px-3.5 !py-1.5 !text-[11px]" data-testid={`quote-transform-${q.ref}`}>
                            <ArrowRightLeft className="h-3.5 w-3.5" /> Transformer en réservation
                          </button>
                        )}
                        {q.reservation_id && <span className="text-xs font-semibold text-sage">→ Réservation créée</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? `Modifier ${editing.ref} (nouvelle version)` : "Nouveau devis"}</DialogTitle>
            <DialogDescription>{editing ? "La modification crée une nouvelle version — l'ancienne est conservée et le devis repasse en brouillon." : "Brouillon modifiable avant envoi au client."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="q-name">Nom du client</label><input id="q-name" className="input-ae" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="quote-customer-name" /></div>
              <div><label className="label-ae" htmlFor="q-email">E-mail du client</label><input id="q-email" type="email" className="input-ae" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="quote-customer-email" /></div>
              <div><label className="label-ae" htmlFor="q-start">Début</label><input id="q-start" type="date" className="input-ae" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="quote-start" /></div>
              <div><label className="label-ae" htmlFor="q-end">Fin</label><input id="q-end" type="date" className="input-ae" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="quote-end" /></div>
              <div><label className="label-ae" htmlFor="q-product">Article</label>
                <select id="q-product" className="input-ae" value={form.product_slug} onChange={(e) => setForm({ ...form, product_slug: e.target.value })} data-testid="quote-product">
                  <option value="">Choisir…</option>
                  {products.map((p) => <option key={p.slug} value={p.slug}>{p.name} — {p.price_ttc.toFixed(2)} €</option>)}
                </select>
              </div>
              <div><label className="label-ae" htmlFor="q-qty">Quantité</label><input id="q-qty" type="number" min="1" className="input-ae" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="quote-qty" /></div>
              <div><label className="label-ae" htmlFor="q-venue">Lieu (facultatif)</label><input id="q-venue" className="input-ae" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="quote-venue" /></div>
              <div><label className="label-ae" htmlFor="q-valid">Validité (jours)</label><input id="q-valid" type="number" min="1" className="input-ae" value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })} data-testid="quote-valid-days" /></div>
            </div>
            <div><label className="label-ae" htmlFor="q-notes">Notes</label><input id="q-notes" className="input-ae" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-notes" /></div>
            <button type="button" onClick={submit} className="btn-primary-ae modal-sticky-btn w-full" data-testid="quote-submit">
              {editing ? "Enregistrer la nouvelle version" : "Créer le brouillon"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
